import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "./tooltip";

/**
 * SearchableSelect - A searchable dropdown component
 * 
 * @param {Array} options - Array of { value, label, color?, icon? } objects
 * @param {string} value - Currently selected value
 * @param {function} onValueChange - Callback when value changes
 * @param {string} placeholder - Placeholder text when nothing is selected
 * @param {string} searchPlaceholder - Placeholder for search input
 * @param {string} emptyText - Text when no results found
 * @param {string} className - Additional CSS classes for trigger button
 * @param {boolean} disabled - Whether the select is disabled
 * @param {React.ReactNode} renderValue - Custom render for selected value
 */
export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  className,
  disabled = false,
  renderValue,
  triggerClassName,
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selectedOption = options.find((opt) => opt.value === value);

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => 
      opt.label.toLowerCase().includes(lower) ||
      (opt.description && opt.description.toLowerCase().includes(lower))
    );
  }, [options, search]);

  const handleSelect = (selectedValue) => {
    // If clicking the already selected value, allow deselecting or just close
    if (selectedValue === value) {
      setOpen(false);
    } else {
      onValueChange(selectedValue);
      setOpen(false);
    }
    setSearch("");
  };

  // Check if label needs tooltip (longer than ~25 chars)
  const needsTooltip = (label) => label && label.length > 25;

  return (
    <TooltipProvider>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            triggerClassName,
            className
          )}
        >
          {selectedOption && needsTooltip(selectedOption.label) ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="truncate flex-1 text-left">
                  <span className="flex items-center gap-2">
                    {selectedOption.color && (
                      <span 
                        className="w-2 h-2 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: selectedOption.color }} 
                      />
                    )}
                    {selectedOption.label}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{selectedOption.label}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="truncate flex-1 text-left">
              {renderValue ? (
                renderValue(selectedOption)
              ) : selectedOption ? (
                <span className="flex items-center gap-2">
                  {selectedOption.color && (
                    <span 
                      className="w-2 h-2 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: selectedOption.color }} 
                    />
                  )}
                  {selectedOption.label}
                </span>
              ) : (
                placeholder
              )}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 min-w-[250px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder} 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 flex-shrink-0",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.color && (
                    <span 
                      className="w-2 h-2 rounded-full mr-2 flex-shrink-0" 
                      style={{ backgroundColor: option.color }} 
                    />
                  )}
                  {needsTooltip(option.label) ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex-1">{option.label}</span>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>{option.label}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="flex-1">{option.label}</span>
                  )}
                  {option.description && (
                    <span className="text-xs text-muted-foreground ml-2 truncate">
                      {option.description}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    </TooltipProvider>
  );
}

export default SearchableSelect;
